import { normalizeSessionScenario } from '../shared/sessionScenario';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import Settings, { type SettingsOpenOptions } from './components/Settings';
import Sidebar from './components/Sidebar';
import Toast from './components/Toast';
import WindowTitleBar from './components/window/WindowTitleBar';
import { CoworkView } from './components/cowork';
import { SkillsView } from './components/skills';
import { prepareSkillConversation } from './services/skillConversation';
import { ScheduledTasksView } from './components/scheduledTasks';
import { ScenariosView } from './components/scenarios';
import ConnectorCatalogSection from './components/connectors/ConnectorCatalogSection';
import CodingView from './coding/CodingView';
import AppDownloadView from './coding/AppDownloadView';
import { LayoutGroup, motion } from 'framer-motion';
import { scenarioService, type MyScenario } from './services/scenario';
import { configService } from './services/config';
import { themeService } from './services/theme';
import { coworkService } from './services/cowork';
import { scheduledTaskService } from './services/scheduledTask';
import { checkForAppUpdate, type AppUpdateInfo, UPDATE_POLL_INTERVAL_MS } from './services/appUpdate';
import { defaultConfig } from './config';
import { setNewTaskPromptMode, setSelectedDesktopClient } from './store/slices/coworkSlice';
import { clearSelection } from './store/slices/quickActionSlice';
import { clearActiveSkills, setActiveSkillIds } from './store/slices/skillSlice';
import { resolveScenarioSkills, type ScenarioSkillEntry } from './services/scenarioSkills';
import { applyScenarioToConversation, captureScenarioConversationTarget, isCurrentScenarioConversationTarget, type ScenarioConversationTarget } from './services/scenarioConversation';
import ScenarioSkillPrepareDialog from './components/scenarios/ScenarioSkillPrepareDialog';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { i18nService } from './services/i18n';
import { matchesShortcut } from './services/shortcuts';
import AppUpdateModal from './components/update/AppUpdateModal';
import LoginPage from './components/auth/LoginPage';
import PlatformStatusBanner from './components/PlatformStatusBanner';
import { authService } from './services/auth';
import type { AuthState } from './types/auth';
import { isWebMode } from './platform';
import { setPlatformBaseUrl } from './constants/auth';
import InitPage from './components/init/InitPage';
import { orgBrandingService, type OrgBranding } from './services/orgBranding';
import OAuthAuthorize from './components/auth/OAuthAuthorize';
import { useIsMobile } from './hooks/useIsMobile';

const MainApp: React.FC = () => {
  const isWeb = isWebMode();
  const [showSettings, setShowSettings] = useState(false);
  const [settingsOptions, setSettingsOptions] = useState<SettingsOpenOptions>({});
  const [mainView, setMainView] = useState<'cowork' | 'skills' | 'scheduledTasks' | 'scenarios' | 'connectors' | 'coding' | 'appDownload'>('cowork');
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastPersistent, setToastPersistent] = useState(false);
  const [, forceLanguageRefresh] = useState(0);
  const isMobile = useIsMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth < 768);
  const [activeScenario, setActiveScenario] = useState<MyScenario | null>(null);
  // 对应 demo 的 entering boolean — 非 null 时触发源卡片起势 (粒子/光环/摇头/底部闪光)
  const [enteringScenarioId, setEnteringScenarioId] = useState<number | null>(null);
  // 场景跳转/取消的 timer handle — 快速来回切换时要取消旧 timer, 否则状态重叠会导致黑屏
  const scenarioTimersRef = useRef<number[]>([]);
  const scenarioEntryRef = useRef(0);
  const scenarioMapRef = useRef<Map<string, MyScenario>>(new Map());
  const scenarioLanguageReloadRef = useRef(0);
  const scenarioRestoreEpochRef = useRef(0);
  const clearScenarioTimers = useCallback(() => {
    // 用户改选/取消后，丢弃旧会话场景补查的迟到结果。
    scenarioRestoreEpochRef.current += 1;
    scenarioEntryRef.current++;
    scenarioTimersRef.current.forEach((id) => window.clearTimeout(id));
    scenarioTimersRef.current = [];
  }, []);
  const [updateInfo, setUpdateInfo] = useState<AppUpdateInfo | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [platformConfigured, setPlatformConfigured] = useState<boolean | null>(null);
  const [orgBranding, setOrgBranding] = useState<OrgBranding | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const hasInitialized = useRef(false);
  const dispatch = useDispatch();
  const currentSessionId = useSelector((state: RootState) => state.cowork.currentSessionId);
  const currentSession = useSelector((state: RootState) => state.cowork.currentSession);
  const isWindows = window.electron.platform === 'win32';

  // 动态更新浏览器标签页标题
  const appName = orgBranding?.code || 'BoMate';
  useEffect(() => {
    document.title = currentSession?.title ? `${currentSession.title} - ${appName}` : appName;
  }, [currentSession?.title, appName]);

  // 初始化应用
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initializeApp = async () => {
      try {
        // 标记平台，用于 CSS 条件样式（如 Windows 标题栏按钮区域留白）
        document.documentElement.classList.add(`platform-${window.electron.platform}`);

        // 初始化配置
        await configService.init();
        
        // 初始化主题
        themeService.initialize();

        // 初始化语言（未登录状态，不持久化）
        await i18nService.initialize(false);

        // 检查 boclaw-platform 是否已配置
        try {
          const platformRes = await fetch('/api/platform/status');
          const platformData = await platformRes.json();
          if (platformData.configured && platformData.url) {
            // 同步到 localStorage（如果浏览器 localStorage 被清除）
            setPlatformBaseUrl(platformData.url);
            setPlatformConfigured(true);
            // 从 localStorage 同步恢复组织品牌（不阻塞初始化），后台异步刷新
            await orgBrandingService.init();
            setOrgBranding(orgBrandingService.getBranding());
          } else {
            setPlatformConfigured(false);
          }
        } catch {
          setPlatformConfigured(false);
        }

        const restoredAuth = await authService.init();
        setAuthState(restoredAuth);

        // 初始化定时任务服务
        await scheduledTaskService.init();

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setInitError(i18nService.t('initializationError'));
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    const unsubscribe = i18nService.subscribe(() => {
      forceLanguageRefresh((prev) => prev + 1);
      if (!authService.getAuthState()) return;

      const reloadId = ++scenarioLanguageReloadRef.current;
      void scenarioService.loadScenarios().then((localizedScenarios) => {
        if (reloadId !== scenarioLanguageReloadRef.current) return;
        const localizedById = new Map(localizedScenarios.map((scenario) => [scenario.id, scenario]));
        for (const [sessionId, scenario] of scenarioMapRef.current.entries()) {
          const localizedScenario = localizedById.get(scenario.id);
          if (!localizedScenario) continue;
          scenarioMapRef.current.set(sessionId, localizedScenario);
          if (sessionId !== '__pending__' && !sessionId.startsWith('temp-')) {
            scenarioService.saveSessionScenario(sessionId, localizedScenario);
          }
        }
        setActiveScenario((scenario) => scenario ? (localizedById.get(scenario.id) || scenario) : null);
      });
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = orgBrandingService.subscribe(() => {
      setOrgBranding(orgBrandingService.getBranding());
    });
    return unsubscribe;
  }, []);

  // 窗口获得焦点时刷新组织品牌信息，确保后台修改后 web 端及时生效
  // 同时校验 token 有效性，失效则自动退出到登录页
  useEffect(() => {
    const handleFocus = () => {
      if (platformConfigured) {
        orgBrandingService.refresh();
      }
      // 校验 platform token 是否仍有效
      authService.verifyToken();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [platformConfigured]);

  useEffect(() => {
    let previousIdentity = authService.getSessionIdentity();
    const unsubscribe = authService.subscribe(async (nextAuthState) => {
      const identity = authService.getSessionIdentity();
      const identityChanged = identity !== previousIdentity;
      previousIdentity = identity;
      setAuthState(nextAuthState);
      // 凭证续期不重建配置和语言；仅真正登录/换号时加载，避免模型与输入状态闪动。
      if (nextAuthState && identityChanged) {
        await configService.init();
        if (identity !== authService.getSessionIdentity()) return;
        await i18nService.initialize(true);
      }
    });
    return unsubscribe;
  }, []);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Renderer] Network online');
      window.electron.networkStatus.send('online');
    };

    const handleOffline = () => {
      console.log('[Renderer] Network offline');
      window.electron.networkStatus.send('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleShowSettings = useCallback((options?: SettingsOpenOptions) => {
    setSettingsOptions({
      initialTab: options?.initialTab,
      notice: options?.notice,
    });
    setShowSettings(true);
  }, []);

  // 活动只可打开本期已实现的独立入口，不能绕过博识空间范围限制。
  useEffect(() => {
    const navigate = (event: Event) => {
      const route = (event as CustomEvent).detail;
      clearScenarioTimers();
      if (route === 'settings:account' || route === 'topup') handleShowSettings({ initialTab: 'account' });
      else if (route === 'skills') setMainView('skills');
      else if (route === 'scenes') setMainView('scenarios');
      else if (route === 'connectors') setMainView('connectors');
    };
    window.addEventListener('campaign:navigate', navigate);
    return () => window.removeEventListener('campaign:navigate', navigate);
  }, [handleShowSettings, clearScenarioTimers]);

  /** 技能使用只切换到待发送草稿，不触发模型或计费；场景与技能身份不能混用。 */
  const handleUseSkill = useCallback((skillId: string, question?: string) => {
    if (!prepareSkillConversation(skillId, question)) return false;
    clearScenarioTimers();
    setActiveScenario(null);
    setMainView('cowork');
    return true;
  }, [clearScenarioTimers]);

  const handleShowSkills = useCallback(() => {
    clearScenarioTimers();
    setMainView('skills');
  }, [clearScenarioTimers]);

  const handleShowCowork = useCallback(() => {
    clearScenarioTimers();
    setMainView('cowork');
  }, [clearScenarioTimers]);

  const handleShowScheduledTasks = useCallback(() => {
    clearScenarioTimers();
    setMainView('scheduledTasks');
  }, [clearScenarioTimers]);

  const handleShowScenarios = useCallback(() => {
    clearScenarioTimers();
    setMainView('scenarios');
  }, [clearScenarioTimers]);

  const handleShowConnectors = useCallback(() => {
    clearScenarioTimers();
    setMainView('connectors');
  }, [clearScenarioTimers]);

  // 跟踪 scenario - session 映射，切换会话时恢复 activeScenario
  useEffect(() => {
    let cancelled = false;
    if (!currentSessionId) {
      if (!scenarioMapRef.current.has('__pending__')) {
        setActiveScenario(null);
      }
      return;
    }
    const map = scenarioMapRef.current;
    if (currentSession?.id === currentSessionId && currentSession.scenario === null) {
      map.delete(currentSessionId);
      scenarioService.removeSessionScenario(currentSessionId);
      setActiveScenario(null);
      return;
    }
    const pending = map.get('__pending__');
    if (pending) {
      map.delete('__pending__');
      map.set(currentSessionId, pending);
    }
    const snapshot = currentSession?.id === currentSessionId
      ? normalizeSessionScenario(currentSession.scenario) : null;
    let scenario = snapshot?.snapshotVersion === 1 ? snapshot : map.get(currentSessionId) || null;
    if (!scenario) {
      for (const [key, value] of map.entries()) {
        if (key.startsWith('temp-')) {
          map.delete(key);
          map.set(currentSessionId, value);
          scenario = value;
          break;
        }
      }
    }
    // 内存 map 未命中 → 从 localStorage 恢复
    if (!scenario) {
      const persisted = scenarioService.getSessionScenario(currentSessionId);
      if (persisted) {
        map.set(currentSessionId, persisted);
        scenario = persisted;
      }
    }
    // 内存与 localStorage 都未命中(换设备/清缓存/重开旧会话) → 用服务端持久化的场景标识
    // 恢复。此前卡片只靠前端瞬时状态，一刷新或换设备就"不显示是什么场景应用了"。
    const scenarioRef = snapshot;
    if (!scenario && scenarioRef) {
      const cached = scenarioService.getScenarios().find((s) => s.id === scenarioRef.id);
      if (cached) {
        map.set(currentSessionId, cached);
        scenario = cached;
      } else {
        // 场景列表尚未加载(直接落到会话页)：异步拉一次再补上，期间不阻塞渲染
        const sid = currentSessionId;
        const epoch = scenarioRestoreEpochRef.current;
        const owner = authService.getAuthState()?.user.id;
        void scenarioService.loadScenarios().then((list) => {
          const full = list.find((s) => s.id === scenarioRef.id);
          if (cancelled || epoch !== scenarioRestoreEpochRef.current
            || owner !== authService.getAuthState()?.user.id || !full || scenarioMapRef.current.has(sid)) return;
          scenarioMapRef.current.set(sid, full);
          scenarioService.saveSessionScenario(sid, full);
          setActiveScenario((prev) => prev ?? full);
        }).catch(() => { /* 旧会话场景补查失败不阻塞对话；不跨会话回填。 */ });
      }
    }
    if (scenario && !currentSessionId.startsWith('temp-')) {
      scenarioService.saveSessionScenario(currentSessionId, scenario);
    }
    setActiveScenario(scenario);
    return () => { cancelled = true; };
  }, [currentSessionId, currentSession?.id, currentSession?.scenario]);

  /** 完成只读检查后原子切换到云端场景草稿；不清空未发送内容、不自动发起模型任务。 */
  const enterScenarioChat = useCallback(async (scenario: MyScenario) => {
    const identity = authService.getSessionIdentity();
    const ticket = ++scenarioEntryRef.current;
    try {
      const resolution = await resolveScenarioSkills(scenario, { force: true });
      if (!identity || identity !== authService.getSessionIdentity() || ticket !== scenarioEntryRef.current) return false;
      clearScenarioTimers();
      scenarioMapRef.current.set('__pending__', scenario);
      setActiveScenario(scenario);
      dispatch(setSelectedDesktopClient({ clientId: null }));
      dispatch(setNewTaskPromptMode('collaboration'));
      coworkService.clearSession();
      dispatch(clearSelection());
      dispatch(setActiveSkillIds(resolution.ready.map(skill => skill.id)));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setEnteringScenarioId(scenario.id);
      scenarioTimersRef.current.push(window.setTimeout(() => {
        setMainView('cowork');
        window.dispatchEvent(new CustomEvent('cowork:focus-input'));
      }, reducedMotion ? 0 : 390));
      scenarioTimersRef.current.push(window.setTimeout(() => setEnteringScenarioId(null), reducedMotion ? 0 : 850));
      return true;
    } catch {
      if (identity === authService.getSessionIdentity() && ticket === scenarioEntryRef.current) setToastMessage(i18nService.t('scenarioDetailsLoadFailed'));
      return false;
    }
  }, [dispatch, clearScenarioTimers]);

  const [skillPrepare, setSkillPrepare] = useState<{ scenario: MyScenario; entries: ScenarioSkillEntry[]; target?: ScenarioConversationTarget } | null>(null);
  useEffect(() => {
    const reset = () => {
      clearScenarioTimers(); scenarioMapRef.current.clear(); scenarioLanguageReloadRef.current++;
      setSkillPrepare(null); setActiveScenario(null); setEnteringScenarioId(null);
    };
    window.addEventListener('auth:identityChanging', reset);
    return () => { clearScenarioTimers(); window.removeEventListener('auth:identityChanging', reset); };
  }, [clearScenarioTimers]);

  /** 仅明确确认后检查并展示安装/启用项；网络失败不能当作已经就绪。 */
  const handleStartScenarioChat = useCallback(async (scenario: MyScenario) => {
    const identity = authService.getSessionIdentity();
    const ticket = ++scenarioEntryRef.current;
    try {
      const resolution = await resolveScenarioSkills(scenario, { force: true });
      if (!identity || identity !== authService.getSessionIdentity() || ticket !== scenarioEntryRef.current) return false;
      if (resolution.entries.some(entry => entry.state !== 'ready')) {
        setSkillPrepare({ scenario, entries: resolution.entries });
        return true;
      }
      return await enterScenarioChat(scenario);
    } catch {
      if (identity === authService.getSessionIdentity() && ticket === scenarioEntryRef.current) setToastMessage(i18nService.t('scenarioDetailsLoadFailed'));
      return false;
    }
  }, [enterScenarioChat]);

  /** 当前对话完成准备后仍重新读取技能；仅更新目标会话上下文，不清会话或导航。 */
  const finishCurrentScenario = useCallback(async (scenario: MyScenario, target: ScenarioConversationTarget) => {
    const ticket = ++scenarioEntryRef.current;
    try {
      const resolution = await resolveScenarioSkills(scenario, { force: true });
      if (ticket !== scenarioEntryRef.current) return false;
      if (!applyScenarioToConversation(target, scenario, resolution)) return false;
      clearScenarioTimers();
      scenarioMapRef.current.set(target.sessionId, scenario);
      setActiveScenario(scenario);
      return true;
    } catch {
      if (isCurrentScenarioConversationTarget(target)) setToastMessage(i18nService.t('scenarioDetailsLoadFailed'));
      return false;
    }
  }, [clearScenarioTimers]);

  /** 输入框选择在当前上下文生效；新草稿沿用新场景入口，已有会话先核验执行状态。 */
  const handleApplyScenarioToConversation = useCallback(async (scenario: MyScenario) => {
    if (!currentSessionId) return handleStartScenarioChat(scenario);
    const target = captureScenarioConversationTarget();
    if (!target) return false;
    const ticket = ++scenarioEntryRef.current;
    try {
      const resolution = await resolveScenarioSkills(scenario, { force: true });
      if (ticket !== scenarioEntryRef.current || !isCurrentScenarioConversationTarget(target)) return false;
      if (resolution.entries.some(entry => entry.state !== 'ready')) {
        setSkillPrepare({ scenario, entries: resolution.entries, target });
        return true;
      }
      return finishCurrentScenario(scenario, target);
    } catch {
      if (isCurrentScenarioConversationTarget(target)) setToastMessage(i18nService.t('scenarioDetailsLoadFailed'));
      return false;
    }
  }, [currentSessionId, handleStartScenarioChat, finishCurrentScenario]);

  /** 静默清除当前场景（不导航）：切快速模式等场合用 —— 与"退出场景"
   *  按钮的 handleDismissScenario（跳回场景页做反向 morph）语义不同。 */
  const handleClearScenarioQuietly = useCallback(() => {
    clearScenarioTimers();
    if (currentSessionId) {
      scenarioMapRef.current.delete(currentSessionId);
      scenarioService.removeSessionScenario(currentSessionId);
    }
    scenarioMapRef.current.delete('__pending__');
    setActiveScenario(null);
  }, [currentSessionId, clearScenarioTimers]);

  const handleDismissScenario = useCallback(() => {
    clearScenarioTimers();

    if (currentSessionId) {
      scenarioMapRef.current.delete(currentSessionId);
      scenarioService.removeSessionScenario(currentSessionId);
    }
    scenarioMapRef.current.delete('__pending__');

    // 反向 morph: banner → 源卡片
    setMainView('scenarios');
    scenarioTimersRef.current.push(
      window.setTimeout(() => {
        setActiveScenario(null);
      }, 800)
    );
  }, [currentSessionId, clearScenarioTimers]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const handleOpenSecretary = useCallback(async () => {
    clearScenarioTimers();
    setActiveScenario(null);
    // 已选技能是"给下一条消息用的"，不该跟着导航漂到 BoMate 来：
    // 新建任务（handleNewChat）一直是清的，这里漏了，于是从场景应用/别的会话
    // 点进 BoMate 后，上一处选中的技能会被当成本轮技能一路带进消息 metadata，
    // 表现为 BoMate 每条消息下面挂一排技能徽标，而普通会话没有。
    dispatch(clearActiveSkills());
    setMainView('cowork');
    await coworkService.openSecretarySession();
  }, [clearScenarioTimers, dispatch]);

  const handleNewChat = useCallback(() => {
    const shouldClearInput = mainView === 'cowork' || !!currentSessionId;
    clearScenarioTimers();
    coworkService.clearSession();
    dispatch(clearSelection());
    dispatch(clearActiveSkills());
    setActiveScenario(null);
    setMainView('cowork');
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('cowork:focus-input', {
        detail: { clear: shouldClearInput },
      }));
    }, 0);
  }, [dispatch, mainView, currentSessionId, clearScenarioTimers]);

  const showToast = useCallback((message: string, options?: { persistent?: boolean }) => {
    setToastMessage(message);
    setToastPersistent(!!options?.persistent);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    if (!options?.persistent) {
      toastTimerRef.current = window.setTimeout(() => {
        setToastMessage(null);
        toastTimerRef.current = null;
      }, 2200);
    }
  }, []);

  const closeToast = useCallback(() => {
    setToastMessage(null);
    setToastPersistent(false);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  const handleShowAccount = useCallback(() => {
    handleShowSettings({ initialTab: 'account' });
  }, [handleShowSettings]);

  // 积分不足等会话错误复用账户入口，保留当前会话以便处理后继续。
  useEffect(() => {
    window.addEventListener('bomate:open-account', handleShowAccount);
    return () => window.removeEventListener('bomate:open-account', handleShowAccount);
  }, [handleShowAccount]);

  const handleLogout = useCallback(async () => {
    await authService.logout();
    // 退出后重新检测浏览器语言（未登录状态）
    await configService.init();
    await i18nService.initialize(false);
    setShowSettings(false);
    showToast(i18nService.t('authLogoutSuccess'));
  }, [showToast]);

  const runUpdateCheck = useCallback(async () => {
    try {
      const currentVersion = await window.electron.appInfo.getVersion();
      const nextUpdate = await checkForAppUpdate(currentVersion);
      setUpdateInfo(nextUpdate);
      if (!nextUpdate) {
        setShowUpdateModal(false);
      }
    } catch (error) {
      console.error('Failed to check app update:', error);
      setUpdateInfo(null);
      setShowUpdateModal(false);
    }
  }, []);

  const handleOpenUpdateModal = useCallback(() => {
    if (!updateInfo) return;
    setShowUpdateModal(true);
  }, [updateInfo]);

  const handleConfirmUpdate = useCallback(async () => {
    if (!updateInfo) return;
    setShowUpdateModal(false);
    try {
      const result = await window.electron.shell.openExternal(updateInfo.url);
      if (!result.success) {
        showToast(i18nService.t('updateOpenFailed'));
      }
    } catch (error) {
      console.error('Failed to open update url:', error);
      showToast(i18nService.t('updateOpenFailed'));
    }
  }, [updateInfo, showToast]);

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const isShortcutInputActive = () => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return false;
    return activeElement.dataset.shortcutInput === 'true';
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isShortcutInputActive()) return;

      const { shortcuts } = configService.getConfig();
      const activeShortcuts = {
        ...defaultConfig.shortcuts,
        ...(shortcuts ?? {}),
      };

      if (matchesShortcut(event, activeShortcuts.newChat)) {
        event.preventDefault();
        handleNewChat();
        return;
      }

      if (matchesShortcut(event, activeShortcuts.search)) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent('cowork:shortcut:search'));
        return;
      }

      if (matchesShortcut(event, activeShortcuts.settings)) {
        event.preventDefault();
        handleShowSettings();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleShowSettings, handleNewChat]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      scenarioTimersRef.current.forEach((id) => window.clearTimeout(id));
      scenarioTimersRef.current = [];
    };
  }, []);

  // Listen for toast events from child components
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string | { message: string; persistent?: boolean }>).detail;
      if (!detail) return;
      if (typeof detail === 'string') {
        showToast(detail);
      } else if (detail.message) {
        showToast(detail.message, { persistent: detail.persistent });
      }
    };
    window.addEventListener('app:showToast', handler);
    return () => window.removeEventListener('app:showToast', handler);
  }, [showToast]);

  // 监听托盘菜单打开设置的 IPC 事件
  useEffect(() => {
    const unsubscribe = window.electron.ipcRenderer.on('app:openSettings', () => {
      handleShowSettings();
    });
    return unsubscribe;
  }, [handleShowSettings]);

  // 监听托盘菜单新建任务的 IPC 事件
  useEffect(() => {
    const unsubscribe = window.electron.ipcRenderer.on('app:newTask', () => {
      handleNewChat();
    });
    return unsubscribe;
  }, [handleNewChat]);

  // 监听定时任务查看会话事件
  useEffect(() => {
    const handleViewSession = async (event: Event) => {
      const { sessionId } = (event as CustomEvent).detail;
      if (sessionId) {
        setMainView('cowork');
        await coworkService.loadSession(sessionId);
      }
    };
    const showCowork = () => setMainView('cowork');
    window.addEventListener('cowork:show', showCowork);
    window.addEventListener('scheduledTask:viewSession', handleViewSession);
    return () => {
      window.removeEventListener('scheduledTask:viewSession', handleViewSession);
      window.removeEventListener('cowork:show', showCowork);
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    let cancelled = false;

    const checkUpdate = async () => {
      if (cancelled) return;
      await runUpdateCheck();
    };

    void checkUpdate();
    const timer = window.setInterval(() => {
      void checkUpdate();
    }, UPDATE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isInitialized, runUpdateCheck]);

  const isOverlayActive = showUpdateModal;
  const windowsStandaloneTitleBar = (isWindows && !isWeb) ? (
    <div className="draggable relative h-9 shrink-0 dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted">
      <WindowTitleBar isOverlayActive={isOverlayActive} />
    </div>
  ) : null;

  // 动态更新 favicon 为组织 logo
  useEffect(() => {
    if (!orgBranding?.hasLogo) return;
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = orgBranding.logoUrl;
  }, [orgBranding?.hasLogo, orgBranding?.logoUrl]);

  if (!isInitialized) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        {windowsStandaloneTitleBar}
        <div className="flex-1 flex items-center justify-center dark:bg-claude-darkBg bg-claude-bg">
          <div className="flex flex-col items-center space-y-6">
            {orgBranding?.hasLogo ? (
              <div className="flex items-center justify-center animate-pulse">
                <img src={orgBranding.logoUrl} alt={orgBranding.code} className="h-20 w-20 object-contain rounded" />
              </div>
            ) : (
              <div className="flex items-center justify-center animate-pulse">
                <img src="/bomate-logo.png" alt="BoMate" className="h-20 w-20 rounded-2xl object-contain" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="dark:text-claude-darkText text-claude-text text-lg font-medium">{i18nService.t('loading').replace(/[.…]+$/, '')}</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-claude-accent animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-claude-accent animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-claude-accent animate-bounce" />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (platformConfigured === false) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        {windowsStandaloneTitleBar}
        <InitPage
          onComplete={() => {
            setPlatformConfigured(true);
          }}
        />
      </div>
    );
  }

  if (initError) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        {windowsStandaloneTitleBar}
        <div className="flex-1 flex flex-col items-center justify-center dark:bg-claude-darkBg bg-claude-bg">
          <div className="flex flex-col items-center space-y-6 max-w-md px-6">
            <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
            <div className="dark:text-claude-darkText text-claude-text text-xl font-medium text-center">{initError}</div>
            <button
              onClick={() => handleShowSettings()}
              className="px-6 py-2.5 bg-claude-accent hover:bg-claude-accentHover text-white rounded-xl shadow-md transition-colors text-sm font-medium"
            >
              {i18nService.t('openSettings')}
            </button>
          </div>
          {showSettings && (
            <Settings
              onClose={handleCloseSettings}
              initialTab={settingsOptions.initialTab}
              notice={settingsOptions.notice}
              authState={authState}
              onLogout={handleLogout}
              onShowToast={showToast}
              onCheckUpdate={runUpdateCheck}
              updateInfo={updateInfo}
            />
          )}
        </div>
      </div>
    );
  }

  if (!authState) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        {windowsStandaloneTitleBar}
        <PlatformStatusBanner onRecovered={() => showToast('已恢复与平台的连接')} />
        <LoginPage onToast={showToast} />
        {toastMessage && (
          <Toast message={toastMessage} persistent={toastPersistent} onClose={closeToast} />
        )}
      </div>
    );
  }

  // Code / App 下载 —— 独立全屏页（设计稿为独立 web 页，隐藏主侧边栏，页面内提供返回入口）
  if (mainView === 'coding' || mainView === 'appDownload') {
    return (
      <div className="h-screen overflow-hidden flex flex-col dark:bg-claude-darkBg bg-claude-bg">
        <PlatformStatusBanner onRecovered={() => showToast('已恢复与平台的连接')} />
        {mainView === 'coding' ? (
          <CodingView onBack={handleShowCowork} isMobile={isMobile} authState={authState} onShowSettings={() => handleShowSettings()} />
        ) : (
          <AppDownloadView onBack={handleShowCowork} isMobile={isMobile} />
        )}
        {showSettings && (
          <Settings
            onClose={handleCloseSettings}
            initialTab={settingsOptions.initialTab}
            notice={settingsOptions.notice}
            authState={authState}
            onLogout={handleLogout}
            onShowToast={showToast}
            onCheckUpdate={runUpdateCheck}
            updateInfo={updateInfo}
          />
        )}
        {toastMessage && (
          <Toast message={toastMessage} persistent={toastPersistent} onClose={closeToast} />
        )}
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col dark:bg-claude-darkBg bg-claude-bg">
      <PlatformStatusBanner onRecovered={() => showToast('已恢复与平台的连接')} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <Sidebar
          authState={authState}
          onShowAccount={handleShowAccount}
          onShowSettings={handleShowSettings}
          activeView={mainView}
          onShowSkills={handleShowSkills}
          onShowCowork={handleShowCowork}
          onShowScheduledTasks={handleShowScheduledTasks}
          onShowScenarios={handleShowScenarios}
          onShowConnectors={handleShowConnectors}
          onNewChat={handleNewChat}
          onOpenSecretary={handleOpenSecretary}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          hasUpdate={!!updateInfo}
          onUpdateClick={handleOpenUpdateModal}
          isMobile={isMobile}
        />
        {/* Mobile overlay backdrop */}
        {isMobile && !isSidebarCollapsed && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={handleToggleSidebar}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="h-full overflow-hidden bg-white dark:bg-claude-darkBg">
            {mainView === 'skills' ? (
              <SkillsView
                onUseSkill={handleUseSkill}
                isSidebarCollapsed={isSidebarCollapsed}
                onToggleSidebar={handleToggleSidebar}
                onNewChat={handleNewChat}
              />
            ) : mainView === 'scheduledTasks' ? (
              <ScheduledTasksView
                isSidebarCollapsed={isSidebarCollapsed}
                onToggleSidebar={handleToggleSidebar}
                onNewChat={handleNewChat}
              />
            ) : mainView === 'connectors' ? (
              <div id="connector-page-root" className="flex h-full flex-col bg-white dark:bg-claude-darkBg">
                <div className="draggable flex h-12 shrink-0 items-center justify-between px-6 lg:px-8">
                  <h1 className="text-xl font-semibold text-claude-text dark:text-claude-darkText">
                    {i18nService.t('connectors')}
                  </h1>
                  <WindowTitleBar inline />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 pt-3 lg:px-8">
                  <p className="mb-7 text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    浏览平台连接器并授权，授权后可在 Cowork 里被智能助手调用。
                  </p>
                  <ConnectorCatalogSection
                    onConnectIM={() => handleShowSettings({ initialTab: 'im' })}
                  />
                </div>
              </div>
            ) : (
              // LayoutGroup 承担 banner ⇄ 源卡片的 morph
              <LayoutGroup>
                {mainView === 'scenarios' ? (
                  <motion.div
                    key="scenarios"
                    className="h-full flex flex-col"
                    initial={{ opacity: 0, scale: 0.985 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ScenariosView
                      isSidebarCollapsed={isSidebarCollapsed}
                      onToggleSidebar={handleToggleSidebar}
                      onNewChat={handleNewChat}
                      onStartScenarioChat={handleStartScenarioChat}
                      enteringScenarioId={enteringScenarioId}
                      layoutCardId={activeScenario?.id ?? null}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="cowork"
                    className="h-full flex flex-col"
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <CoworkView
                      onRequestAppSettings={handleShowSettings}
                      onShowSkills={handleShowSkills}
                      isSidebarCollapsed={isSidebarCollapsed}
                      onToggleSidebar={handleToggleSidebar}
                      onNewChat={handleNewChat}
                      authState={authState}
                      isMobile={isMobile}
                      activeScenario={activeScenario}
                      onSelectScenario={handleApplyScenarioToConversation}
                      onShowScenarios={handleShowScenarios}
                      onClearScenario={handleClearScenarioQuietly}
                      onDismissScenario={handleDismissScenario}
                    />
                  </motion.div>
                )}
              </LayoutGroup>
            )}
          </div>
        </div>
      </div>

      {showUpdateModal && updateInfo && (
        <AppUpdateModal
          latestVersion={updateInfo.latestVersion}
          onCancel={() => setShowUpdateModal(false)}
          onConfirm={handleConfirmUpdate}
        />
      )}
      {showSettings && (
        <Settings
          onClose={handleCloseSettings}
          initialTab={settingsOptions.initialTab}
          notice={settingsOptions.notice}
          authState={authState}
          onLogout={handleLogout}
          onShowToast={showToast}
          onCheckUpdate={runUpdateCheck}
          updateInfo={updateInfo}
        />
      )}
      {toastMessage && (
        <Toast message={toastMessage} persistent={toastPersistent} onClose={closeToast} />
      )}
      {skillPrepare && (
        <ScenarioSkillPrepareDialog
          scenario={skillPrepare.scenario}
          entries={skillPrepare.entries}
          onCancel={() => setSkillPrepare(null)}
          onProceed={() => {
            const { scenario, target } = skillPrepare;
            setSkillPrepare(null);
            if (target) void finishCurrentScenario(scenario, target);
            else void enterScenarioChat(scenario);
          }}
        />
      )}
    </div>
  );
};

const App: React.FC = () => {
  // OAuth 授权页面 —— 独立渲染，不走主应用初始化流程
  if (window.location.pathname === '/oauth/authorize') {
    return <OAuthAuthorize />;
  }

  return <MainApp />;
};

export default App;
